/**
 * Schema validation utility using AJV
 * Explicitly rejects malformed responses with detailed error messages
 */

import Ajv, { ErrorObject } from 'ajv';
// Note: ajv-formats removed to avoid bundle resolution issues during client build
// If you need strict format validation in server environments, add it back there.

import {
  agentResponseSchema,
  emailMessageSchema,
  calendarEventSchema,
  oauthTokenResponseSchema,
  emailActionParamsSchema,
  userPreferencesSchema,
  workflowNodeBaseSchema,
  aiNodeSchema,
  conditionNodeSchema,
  ACTION_SCHEMAS,
  aiClassificationOutputSchema,
  aiExtractionOutputSchema,
  aiGenerationOutputSchema,
  aiResearchOutputSchema,
  aiAnalysisOutputSchema,
  relationshipAnalysisOutputSchema,
  contactExtractionOutputSchema
} from './schemas';
import { validationTelemetry } from './validationTelemetry';

// Initialize AJV with strict validation
const ajv = new Ajv({
  allErrors: true,
  verbose: true,
  strict: true,
  strictTypes: true,
  allowUnionTypes: true, // Allow union types like ['string', 'boolean', 'number'] for SQLite compatibility
  validateFormats: true,
  removeAdditional: false // Keep additional properties but validate required ones
});

// Format validation (email, date-time, uri, etc.) is intentionally omitted in the browser bundle
// to avoid bundling issues with ajv-formats. Server-side code can register formats if needed.

// Compile schemas for better performance
const validators = {
  agentResponse: ajv.compile(agentResponseSchema),
  emailMessage: ajv.compile(emailMessageSchema),
  calendarEvent: ajv.compile(calendarEventSchema),
  oauthTokenResponse: ajv.compile(oauthTokenResponseSchema),
  emailActionParams: ajv.compile(emailActionParamsSchema),
  userPreferences: ajv.compile(userPreferencesSchema),
  workflowNodeBase: ajv.compile(workflowNodeBaseSchema),
  aiNode: ajv.compile(aiNodeSchema),
  conditionNode: ajv.compile(conditionNodeSchema),
  // AI output validators
  aiClassificationOutput: ajv.compile(aiClassificationOutputSchema),
  aiExtractionOutput: ajv.compile(aiExtractionOutputSchema),
  aiGenerationOutput: ajv.compile(aiGenerationOutputSchema),
  aiResearchOutput: ajv.compile(aiResearchOutputSchema),
  aiAnalysisOutput: ajv.compile(aiAnalysisOutputSchema),
  relationshipAnalysisOutput: ajv.compile(relationshipAnalysisOutputSchema),
  contactExtractionOutput: ajv.compile(contactExtractionOutputSchema)
};

// Compile action schemas
const actionValidators: Record<string, any> = {};
for (const [actionName, schema] of Object.entries(ACTION_SCHEMAS)) {
  actionValidators[actionName] = ajv.compile(schema);
}

export interface ValidationResult {
  valid: boolean;
  errors?: ErrorObject[];
  errorMessage?: string;
}

/**
 * Format AJV errors into a human-readable message
 */
function formatErrors(errors: ErrorObject[] | null | undefined): string {
  if (!errors || errors.length === 0) {
    return 'Unknown validation error';
  }

  return errors.map(err => {
    const path = err.instancePath || 'root';
    const message = err.message || 'validation failed';
    const dataPreview = typeof err.data === 'object' 
      ? JSON.stringify(err.data).substring(0, 100) + '...'
      : String(err.data);
    
    if (err.keyword === 'required') {
      return `Missing required field: ${err.params.missingProperty}`;
    }
    
    if (err.keyword === 'type') {
      return `Invalid type at ${path}: expected ${err.params.type}, got ${typeof err.data} (value: ${dataPreview})`;
    }
    
    if (err.keyword === 'format') {
      return `Invalid format at ${path}: expected ${err.params.format} (value: ${dataPreview})`;
    }
    
    if (err.keyword === 'enum') {
      return `Invalid value at ${path}: must be one of [${err.params.allowedValues?.join(', ')}] (got: ${dataPreview})`;
    }
    
    if (err.keyword === 'minLength') {
      return `Value at ${path} is too short: minimum length is ${err.params.limit} (got: ${dataPreview})`;
    }
    
    if (err.keyword === 'minimum' || err.keyword === 'maximum') {
      return `Value at ${path} is out of range: ${message} (value: ${dataPreview})`;
    }
    
    return `${path}: ${message} (value: ${dataPreview})`;
  }).join('; ');
}

/**
 * Validate data against a schema with telemetry
 */
function validate(
  data: unknown,
  validator: (data: unknown) => boolean,
  schemaName: string,
  context?: { userId?: string; workflowId?: string; nodeId?: string; actionType?: string }
): ValidationResult {
  try {
    const valid = validator(data);
    
    if (!valid) {
      const errors = validator.errors || [];
      const errorMessage = `Schema validation failed for ${schemaName}: ${formatErrors(errors)}`;
      
      // Log to telemetry system with full context
      const failure = validationTelemetry.logValidationFailure(
        schemaName,
        errors,
        data,
        context || {}
      );
      
      // Return validation result with user-facing diagnostic
      return {
        valid: false,
        errors,
        errorMessage: failure.userDiagnostic
      };
    }
    
    return { valid: true };
  } catch (error) {
    const errorMessage = `Validation exception for ${schemaName}: ${error instanceof Error ? error.message : 'Unknown error'}`;
    console.error('❌ Validation Exception:', error);
    
    // Log exception to telemetry
    validationTelemetry.logValidationFailure(
      schemaName,
      [{
        keyword: 'exception',
        instancePath: '',
        schemaPath: '',
        params: {},
        message: error instanceof Error ? error.message : 'Unknown error',
        data
      }],
      data,
      context || {}
    );
    
    return {
      valid: false,
      errorMessage
    };
  }
}

/**
 * Validate and throw if invalid
 */
function validateOrThrow(
  data: unknown,
  validator: (data: unknown) => boolean,
  schemaName: string
): void {
  const result = validate(data, validator, schemaName);
  if (!result.valid) {
    throw new Error(result.errorMessage);
  }
}

// Export validation functions
export const validator = {
  // Validate methods (return ValidationResult)
  validateAgentResponse: (data: unknown): ValidationResult =>
    validate(data, validators.agentResponse, 'AgentResponse'),
  
  validateEmailMessage: (data: unknown): ValidationResult =>
    validate(data, validators.emailMessage, 'EmailMessage'),
  
  validateCalendarEvent: (data: unknown): ValidationResult =>
    validate(data, validators.calendarEvent, 'CalendarEvent'),
  
  validateOAuthTokenResponse: (data: unknown): ValidationResult =>
    validate(data, validators.oauthTokenResponse, 'OAuthTokenResponse'),
  
  validateEmailActionParams: (data: unknown): ValidationResult =>
    validate(data, validators.emailActionParams, 'EmailActionParams'),
  
  validateUserPreferences: (data: unknown): ValidationResult =>
    validate(data, validators.userPreferences, 'UserPreferences'),

  // Validate or throw methods (throw error if invalid)
  validateAgentResponseOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.agentResponse, 'AgentResponse'),
  
  validateEmailMessageOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.emailMessage, 'EmailMessage'),
  
  validateCalendarEventOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.calendarEvent, 'CalendarEvent'),
  
  validateOAuthTokenResponseOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.oauthTokenResponse, 'OAuthTokenResponse'),
  
  validateEmailActionParamsOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.emailActionParams, 'EmailActionParams'),
  
  validateUserPreferencesOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.userPreferences, 'UserPreferences'),

  // Workflow node validation
  validateWorkflowNode: (data: unknown): ValidationResult =>
    validate(data, validators.workflowNodeBase, 'WorkflowNode'),
  
  validateWorkflowNodeOrThrow: (data: unknown): void =>
    validateOrThrow(data, validators.workflowNodeBase, 'WorkflowNode'),

  // Workflow node config validation based on type
  validateWorkflowNodeConfig: (node: any): ValidationResult => {
    // First validate base structure
    const baseResult = validate(node, validators.workflowNodeBase, 'WorkflowNode');
    if (!baseResult.valid) {
      return baseResult;
    }

    // Then validate config based on node type
    const { type, title, config } = node;

    if (type === 'ai') {
      return validate(config, validators.aiNode, `AI Node (${title})`);
    }

    if (type === 'condition' || type === 'branch') {
      return validate(config, validators.conditionNode, `Condition Node (${title})`);
    }

    if (type === 'action') {
      // Check if we have a schema for this action
      const actionValidator = actionValidators[title] || actionValidators[title.toLowerCase().replace(/\s+/g, '_')];
      if (actionValidator) {
        return validate(config, actionValidator, `Action Node (${title})`);
      }
    }

    // No specific validation for this node type/action
    return { valid: true };
  },

  validateWorkflowNodeConfigOrThrow: (node: any): void => {
    const result = validator.validateWorkflowNodeConfig(node);
    if (!result.valid) {
      throw new Error(result.errorMessage);
    }
  },

  // AI Output Validators
  validateAIClassificationOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.aiClassificationOutput, 'AIClassificationOutput', context),
  
  validateAIExtractionOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.aiExtractionOutput, 'AIExtractionOutput', context),
  
  validateAIGenerationOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.aiGenerationOutput, 'AIGenerationOutput', context),
  
  validateAIResearchOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.aiResearchOutput, 'AIResearchOutput', context),
  
  validateAIAnalysisOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.aiAnalysisOutput, 'AIAnalysisOutput', context),
  
  validateRelationshipAnalysisOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.relationshipAnalysisOutput, 'RelationshipAnalysisOutput', context),
  
  validateContactExtractionOutput: (data: unknown, context?: any): ValidationResult =>
    validate(data, validators.contactExtractionOutput, 'ContactExtractionOutput', context),

  // AI Output Validators (throw on error)
  validateAIClassificationOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.aiClassificationOutput, 'AIClassificationOutput'),
  
  validateAIExtractionOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.aiExtractionOutput, 'AIExtractionOutput'),
  
  validateAIGenerationOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.aiGenerationOutput, 'AIGenerationOutput'),
  
  validateAIResearchOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.aiResearchOutput, 'AIResearchOutput'),
  
  validateAIAnalysisOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.aiAnalysisOutput, 'AIAnalysisOutput'),
  
  validateRelationshipAnalysisOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.relationshipAnalysisOutput, 'RelationshipAnalysisOutput'),
  
  validateContactExtractionOutputOrThrow: (data: unknown, context?: any): void =>
    validateOrThrow(data, validators.contactExtractionOutput, 'ContactExtractionOutput')
};

/**
 * Generic validator for custom schemas
 */
export function createValidator(schema: object, name: string) {
  const compiledValidator = ajv.compile(schema);
  
  return {
    validate: (data: unknown): ValidationResult =>
      validate(data, compiledValidator, name),
    
    validateOrThrow: (data: unknown): void =>
      validateOrThrow(data, compiledValidator, name)
  };
}

export default validator;
